import React, { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/compat/router";
import { storyblokEditable } from "@storyblok/react";
import { analytics } from "@lib/trackers/analytics";
import ahoy from "@lib/ahoy";
import { useCurrentUser } from "@hooks/use-current-user";
import Link from "@components/core/Link";
import ProductSliderV2Gallery from "./ProductSliderV2Gallery";
import { getListingPageInformation } from "@utils/listingPage";
import { getProducts } from "@utils/call-api/products-call";
import { userState } from "@utils/constants/user";

function ProductSliderV2({ blok }) {
  const t = useTranslations("product");

  const router = useRouter();
  const user = useCurrentUser();
  const userGroup = user?.user_group;

  const [products, setProducts] = useState([]);
  const [listingPage, setListingPage] = useState({});

  const getUserGroup = useCallback(() => {
    if (!user) return null;
    if (user.user_group.includes("trying")) return "trying";

    return "confirmed";
  }, [user]);

  const listingPageUrl = () => `/${listingPage?.hub?.slug}/${listingPage?.slug}`;

  function sendTrackerData() {
    const defaultEventData = {
      tracking_name: "homeProductSlider",
      page: router.asPath,
      listingPage: listingPage?.slug,
      position: "",
      product_slug: "",
      user_group: userGroup,
    };

    ahoy("$click", defaultEventData);
    analytics.track("$click", defaultEventData);
  }

  const productsListViewEvent = useCallback(
    (productsData) => {
      const productsUids = productsData.map((product) => product.objectID);

      if (!productsUids || user?.state !== userState?.subscriber) return;

      analytics.track("products_list_view", {
        source: "home",
        hub_or_listing_page_slug: blok.listing_page_slug,
        product_uids: productsUids,
        filters: {},
        user_group: getUserGroup(),
        origin: "next_js",
        ab_test: {},
      });
    },
    [blok.listing_page_slug, getUserGroup],
  );

  useEffect(() => {
    async function fetchListingPageAndProducts() {
      if (!blok?.listing_page_slug) return;

      const listingPageRes = await getListingPageInformation(blok.listing_page_slug);
      const productsRes = await getProducts(
        listingPageRes?.hub?.context,
        listingPageRes?.search_query,
      );

      setProducts(productsRes?.data || []);
      setListingPage(listingPageRes);

      const productsData = productsRes?.data;

      if (productsData?.length > 4) {
        productsListViewEvent(productsData);
      }
    }

    fetchListingPageAndProducts();
  }, [blok.listing_page_slug]);

  if (!blok) return null;
  if (products?.length > 0 && products?.length <= 4) return null;

  return (
    <section
      key={blok._uid}
      {...storyblokEditable(blok)}
      className="storyblok-home-container-full-width"
    >
      <div className="text-center">
        <h4 className="mb-2">{blok.title}</h4>
        {blok.subtitle && <p className="text-gray mb-2 font-size-sm">{blok.subtitle}</p>}
        <div className="mb-4">
          <Link
            href={listingPageUrl()}
            prefetch={false}
            onClick={() => sendTrackerData()}
            className="link-primary font-size-sm"
            noUnderline
          >
            {t("see_more")}
          </Link>
        </div>
      </div>
      <ProductSliderV2Gallery
        products={products.slice(0, 30)}
        listingPage={listingPage}
      />
    </section>
  );
}

export default ProductSliderV2;
